import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import authService from '../../../../../../../utilities/services/auth.service'
import { redeemFlexTicketsAtCheckout } from '../../../../../../../utilities/api'
import { isCartExpiredResponse } from '../../../../../../../utilities/helpers'
import { useTimeout } from '../../../../providers/TimeoutProvider/TimeoutProvider'

import Stack from 'react-bootstrap/Stack'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'

import { Spinner } from '../../../../../../LoadingContainer/Spinner'

const CODE_LENGTH = 6

const errorMessage = (error) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  'Could not redeem these flex tickets. Please try again.'

export default function FlexTicketRedeem({
  cart,
  pendingOrderId,
  donationAmount = 0,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { setIsPurchasing, handleCartExpired } = useTimeout()
  const tickets = Array.isArray(cart?.tickets) ? cart.tickets : []
  const ticketCount = tickets.length
  const offersAllowRedeem =
    ticketCount > 0 &&
    tickets.every((ticket) => ticket?.offer?.redeemWithVouchersOnline === true)
  const canRedeemHere =
    offersAllowRedeem &&
    !cart?.flex_pack &&
    !cart?.package &&
    !cart?.access_pass_template &&
    cart?.event?.organization?.setting?.redeemVouchersOnline === true
  const [codes, setCodes] = useState(() =>
    Array.from({ length: ticketCount }, () => '')
  )
  const [error, setError] = useState('')
  const [isRedeeming, setIsRedeeming] = useState(false)

  if (!canRedeemHere) return null

  const signedIn = authService.isLoggedIn()
  const donationBlocksRedeem = Number(donationAmount) > 0
  const ready =
    signedIn &&
    pendingOrderId &&
    !donationBlocksRedeem &&
    codes.length === ticketCount &&
    codes.every((code) => /^\d{6}$/.test(code)) &&
    new Set(codes).size === codes.length

  const updateCode = (index, value) => {
    const nextValue = String(value || '')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH)
    setCodes((current) => {
      const next = [...current]
      next[index] = nextValue
      return next
    })
    if (error) setError('')
  }

  const redeem = async (event) => {
    event.preventDefault()
    if (!ready || isRedeeming) return
    setIsRedeeming(true)
    setIsPurchasing(true)
    setError('')
    try {
      const response = await redeemFlexTicketsAtCheckout({
        orderId: pendingOrderId,
        codes,
      })
      sessionStorage.setItem('order', JSON.stringify({ id: response.data?.id }))
      navigate(
        `/checkout/checkout-success?intentId=${encodeURIComponent(response.data?.paymentIntentId)}`,
        { replace: true }
      )
    } catch (redeemError) {
      setIsPurchasing(false)
      setIsRedeeming(false)
      if (isCartExpiredResponse(redeemError)) {
        handleCartExpired()
        return
      }
      setError(errorMessage(redeemError))
    }
  }

  return (
    <section className="mb-4">
      <h6 className="form-label">Flex tickets</h6>
      <p className="caption text-muted mb-3">
        Have flex ticket codes? Enter one code per ticket to redeem them instead
        of paying by card.
      </p>
      {!signedIn ? (
        <Stack direction="horizontal" gap={3}>
          <span className="small">Sign in to redeem your flex tickets.</span>
          <Button
            type="button"
            size="sm"
            variant="outline-light"
            onClick={() => navigate('/login', { state: { from: location } })}
          >
            Sign in
          </Button>
        </Stack>
      ) : !pendingOrderId ? (
        <p className="small mb-0">
          Checkout is still preparing. You can enter your flex ticket codes in a
          moment.
        </p>
      ) : (
        <Form onSubmit={redeem}>
          <Stack gap={3}>
            {codes.map((code, index) => (
              <Form.Group key={index} controlId={`flex-ticket-code-${index}`}>
                <Form.Label>
                  {ticketCount === 1
                    ? 'Flex ticket code'
                    : `Ticket ${index + 1} code`}
                </Form.Label>
                <Form.Control
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={CODE_LENGTH}
                  placeholder="6-digit code"
                  value={code}
                  disabled={isRedeeming || donationBlocksRedeem}
                  onChange={(event) => updateCode(index, event.target.value)}
                />
              </Form.Group>
            ))}
            {codes.every((code) => code.length === CODE_LENGTH) &&
              new Set(codes).size !== codes.length && (
                <Alert variant="warning" className="mb-0">
                  Each ticket needs a different code.
                </Alert>
              )}
            {donationBlocksRedeem && (
              <Alert variant="warning" className="mb-0">
                Flex ticket codes cover the tickets in this order. Remove the
                donation to redeem them, or pay the full total by card.
              </Alert>
            )}
            {error && (
              <Alert variant="danger" className="mb-0">
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="outline-light"
              disabled={!ready || isRedeeming}
            >
              {isRedeeming ? <Spinner variant="dark" /> : 'Redeem flex tickets'}
            </Button>
          </Stack>
        </Form>
      )}
    </section>
  )
}
